import { toSortedCompat } from '@sweetistics/contracts/utils/sort';
import { utilsLogger } from '@/lib/logger';

// Treat common marketing/share prefixes as disposable so newsletter URLs collapse to a single canonical.
const TRACKING_PARAM_PREFIXES = [
  'utm_',
  'icmp_',
  'mkt_',
  'mc_',
  'ga_',
  'hs_',
  'vero_',
  'oly_',
  'rb_',
  'wicked_',
  'invite_',
  'share_',
];

const TRACKING_PARAM_NAMES = new Set([
  'ref',
  'ref_src',
  'ref_url',
  'spm',
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'igshid',
  'msclkid',
  'mkcid',
  'mkt_tok',
  'campaignid',
  'adgroupid',
  'adid',
  'chn',
  'soc_src',
  'soc_trk',
  'yclid',
  'dclid',
  'ck_subscriber_id',
  'vero_conv',
  'vero_id',
  'oly_enc_id',
  'oly_anon_id',
  'mc_cid',
  'mc_eid',
  's',
  'source',
  'email_token',
  // Substack-style previews and newsletter share toggles; removing them keeps the canonical key stable.
  'open',
  'open_id',
  'open_email',
  'open_source',
  'open_note',
  'share',
  'share_id',
  'share_token',
  'sharecode',
  'via',
  'redirect',
  'redirect_to',
  'redirect_url',
  'redirect_uri',
  'invite',
  'invite_id',
  'invite_code',
  'invite_token',
  'preview',
  'preview_id',
  'si', // twitter sharing
]);

const DEFAULT_PROTOCOL = 'https:';

type HostCanonicalRule = {
  canonicalHost: string;
  transform?: (url: URL) => void;
  queryAllowList?: string[];
  queryRemove?: string[];
};

const HOST_CANONICAL_RULES: Record<string, HostCanonicalRule> = {
  'youtu.be': {
    canonicalHost: 'youtube.com',
    transform: (url) => {
      const pathSegment = url.pathname.split('/').find((segment) => segment.length > 0);
      const videoId = pathSegment ?? '';
      if (videoId.length > 0 && !url.searchParams.has('v')) {
        url.searchParams.set('v', videoId);
      }
      url.pathname = '/watch';
    },
    queryAllowList: ['v', 'list', 't', 'start', 'end', 'index', 'pp'],
  },
  'm.youtube.com': {
    canonicalHost: 'youtube.com',
    queryAllowList: ['v', 'list', 't', 'start', 'end', 'index', 'pp'],
  },
  'youtube.com': {
    canonicalHost: 'youtube.com',
    queryAllowList: ['v', 'list', 't', 'start', 'end', 'index', 'pp'],
  },
};

export interface CanonicalUrlResult {
  canonicalKey: string;
  canonicalUrl: string;
  displayHost: string;
  displayPath: string;
}

function removeTrackingParameters(searchParameters: URLSearchParams): void {
  const toDelete: string[] = [];

  for (const [key] of searchParameters.entries()) {
    const lowerKey = key.toLowerCase();
    if (TRACKING_PARAM_NAMES.has(lowerKey)) {
      toDelete.push(key);
      continue;
    }
    if (TRACKING_PARAM_PREFIXES.some((prefix) => lowerKey.startsWith(prefix))) {
      toDelete.push(key);
    }
  }

  for (const key of toDelete) {
    searchParameters.delete(key);
  }
}

function sanitizeHost(hostname: string): string {
  if (!hostname) {
    return hostname;
  }
  const trimmed = hostname.trim().toLowerCase();
  if (trimmed.startsWith('www.')) {
    return trimmed.slice(4);
  }
  return trimmed;
}

function applyHostQueryRules(url: URL, rule: HostCanonicalRule | undefined): void {
  if (!rule) {
    return;
  }

  if (rule.queryRemove && rule.queryRemove.length > 0) {
    const removals = new Set(rule.queryRemove.map((parameter) => parameter.toLowerCase()));
    for (const key of url.searchParams.keys()) {
      if (removals.has(key.toLowerCase())) {
        url.searchParams.delete(key);
      }
    }
  }

  if (rule.queryAllowList && rule.queryAllowList.length > 0) {
    const allowList = new Set(rule.queryAllowList.map((parameter) => parameter.toLowerCase()));
    for (const key of url.searchParams.keys()) {
      if (!allowList.has(key.toLowerCase())) {
        url.searchParams.delete(key);
      }
    }
  }
}

function sortSearchParametersNormalized(searchParameters: URLSearchParams): void {
  if (!searchParameters || typeof searchParameters.entries !== 'function') {
    return;
  }
  const entries = toSortedCompat([...searchParameters.entries()], ([aKey, aValue], [bKey, bValue]) => {
    const keyDiff = aKey.localeCompare(bKey);
    if (keyDiff !== 0) {
      return keyDiff;
    }
    return aValue.localeCompare(bValue);
  });

  for (const key of searchParameters.keys()) {
    searchParameters.delete(key);
  }

  for (const [key, value] of entries) {
    searchParameters.append(key, value);
  }
}

function normalizePath(pathname: string): string {
  if (!pathname || pathname === '/') {
    return '';
  }
  return pathname.replaceAll(/\/+$/g, '');
}

export function canonicalizeExternalUrl(rawUrl: string | null | undefined): CanonicalUrlResult | null {
  if (!rawUrl || typeof rawUrl !== 'string') {
    return null;
  }

  const trimmed = rawUrl.trim();
  if (!trimmed) {
    return null;
  }

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    try {
      parsed = new URL(`${DEFAULT_PROTOCOL}//${trimmed}`);
    } catch (error) {
      utilsLogger.warn('canonicalizeExternalUrl: unable to parse URL', {
        url: trimmed,
        error: error instanceof Error ? error.message : String(error),
      });
      return null;
    }
  }

  const protocol = parsed.protocol ? parsed.protocol.toLowerCase() : DEFAULT_PROTOCOL;
  const port = parsed.port ? `:${parsed.port}` : '';
  let host = sanitizeHost(parsed.hostname);
  if (!host) {
    return null;
  }

  const aliasRule = HOST_CANONICAL_RULES[host];
  if (aliasRule) {
    host = aliasRule.canonicalHost;
    aliasRule.transform?.(parsed);
  }

  removeTrackingParameters(parsed.searchParams);
  applyHostQueryRules(parsed, HOST_CANONICAL_RULES[host]);
  sortSearchParametersNormalized(parsed.searchParams);

  const normalizedPath = normalizePath(parsed.pathname);
  const search = parsed.searchParams.toString();

  const canonicalHost = `${host}${port}`;
  const querySuffix = search && `?${search}`;
  const canonicalKey = `${canonicalHost}${normalizedPath || ''}${querySuffix || ''}`;

  let displayPath = normalizedPath || '';
  if (search && host === 'youtube.com' && normalizedPath === '/watch') {
    const videoId = parsed.searchParams.get('v');
    if (videoId) {
      displayPath = `${normalizedPath}?v=${videoId}`;
    }
  }

  const canonicalUrl = `${protocol}//${canonicalKey}`;

  return {
    canonicalUrl,
    canonicalKey,
    displayHost: host,
    displayPath,
  };
}

export function canonicalKeyForComparison(rawUrl: string | null | undefined): string | null {
  if (!rawUrl || typeof rawUrl !== 'string') {
    return null;
  }

  const trimmed = rawUrl.trim();
  if (!trimmed) {
    return null;
  }

  const canonical = canonicalizeExternalUrl(trimmed);
  if (canonical) {
    return canonical.canonicalKey;
  }

  return trimmed.toLowerCase();
}
